import { Currencies } from '@/entities/curency'
import AttentionIcon from '@/shared/assets/icons/attention.svg?react'
import { BaseButton, BaseCheckbox, BaseDialog, BaseInput } from '@/shared/components'
import { BaseCopyButton } from '@/shared/components/BaseCopyButton/BaseCopyButton'
import { ItemLayout } from '@/shared/components/ItemLayout/ItemLayout'
import { PayoutIcon } from '@/shared/components/icons/PayoutIcon'
import { formatCurrency } from '@/shared/lib/currency'
import { formatDate } from '@/shared/lib/date'
import { convertFileToBase64, FileData, useIsMobile, validateFile } from '@/shared/utils'
import { getBankMeta } from '@/shared/utils/getBankMeta'
import { CirclePlus, TrashIcon } from 'lucide-react'
import React, { useCallback, useEffect, useState } from 'react'
import {
  ConfirmSendData,
  StartPayingWithdrawModel,
  WithdrawFile,
  WithdrawStatusIcons,
  WithdrawStatusTitles,
} from '../../model'
import { formatRequisite } from '@/shared/lib/requisite/formatRequisite'
import { FileViewDialog } from '@/features/withdraws/dialogs/FileViewDialog'
import { useReceiptDetail } from '@/features/withdraws/hooks/useReceiptDetail'
import { useReceiptManagement } from '@/features/withdraws'
import { CopyBlock } from '@/shared/components/CopyBlock/CopyBlock'

type Props = {
  isOpen: boolean
  onClose: () => void
  onAction: (data: ConfirmSendData, onSuccessCallback?: () => void) => void
  onCancel: () => void
  withdraw: StartPayingWithdrawModel
  isLoading: boolean
}

export const ActivePayoutDialog = ({
  isOpen,
  onClose,
  onAction,
  onCancel,
  withdraw,
  isLoading,
}: Props) => {
  const isMobile = useIsMobile()
  const [confirm, setConfirm] = useState(false)
  const { icon: bankIcon, title: bankTitle } = getBankMeta(withdraw.bankToName, 'w-10 h-10')

  const [uploadedFiles, setUploadedFiles] = useState<FileData[]>([])
  const [uploadedPreviouslyFiles, setUploadedPreviouslyFiles] = useState<WithdrawFile[]>([])

  const [fileErrors, setFileErrors] = useState<string[]>([]) // Изменено на массив ошибок
  const [isUploading, setIsUploading] = useState(false)

  const [confirmError, setConfirmError] = useState(false)
  const [fileRequiredError, setFileRequiredError] = useState(false) // Новое состояние для ошибки обязательного файла
  const { deleteReceipt } = useReceiptManagement()
  const { data: withdrawFilesData } = useReceiptDetail(withdraw.id, isOpen)
  const [factAmount, setFactAmount] = useState<number | undefined>(withdraw.amountTo)
  const [showFactAmountConfirm, setShowFactAmountConfirm] = useState(false)

  useEffect(() => {
    if (withdrawFilesData) setUploadedPreviouslyFiles(withdrawFilesData)
  }, [withdrawFilesData])

  const handleSubmit = () => {
    const hasConfirm = confirm
    const hasFiles =
      uploadedFiles.length > 0 || (uploadedPreviouslyFiles && uploadedPreviouslyFiles.length > 0) // Проверяем наличие файлов

    setConfirmError(!hasConfirm)
    setFileRequiredError(!hasFiles) // Устанавливаем ошибку если файлов нет

    const factAmountError = factAmount! > withdraw.amountTo || !factAmount || factAmount < 0

    if (!hasConfirm || !hasFiles || factAmountError) return

    const data: ConfirmSendData = {
      sentAmount: factAmount,
      withdrawId: withdraw.id,
      recieptFileAttacments: uploadedFiles.map((file) => ({
        originalFileName: file.name,
        base64FileData: file.base64,
      })),
    }

    onAction(data, () => onClose())
  }

  const handleFilesUpload = useCallback(async (files: FileList) => {
    setFileErrors([])
    setFileRequiredError(false) // Сбрасываем ошибку при загрузке файлов
    setIsUploading(true)

    const errors: string[] = []
    const validFiles: FileData[] = []

    // Обрабатываем все файлы параллельно
    const filePromises = Array.from(files).map(async (file) => {
      try {
        const validationError = validateFile(file, false)
        if (validationError) {
          errors.push(`${file.name}: ${validationError}`)
          return null
        }

        return await convertFileToBase64(file)
      } catch (error) {
        errors.push(`${file.name}: Ошибка при загрузке файла`)
        return null
      }
    })

    try {
      const results = await Promise.all(filePromises)

      // Фильтруем успешно загруженные файлы
      results.forEach((result) => {
        if (result) {
          validFiles.push(result)
        }
      })

      // Добавляем успешно загруженные файлы к существующим
      if (validFiles.length > 0) {
        setUploadedFiles((prev) => [...prev, ...validFiles])
      }

      // Устанавливаем ошибки, если они есть
      if (errors.length > 0) {
        setFileErrors(errors)
      }
    } catch (error) {
      setFileErrors(['Произошла ошибка при загрузке файлов'])
    } finally {
      setIsUploading(false)
    }
  }, [])

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (files && files.length > 0) {
      handleFilesUpload(files)
    }
    event.target.value = '' // Очищаем input для возможности загрузки тех же файлов повторно
  }

  const handleFileRemove = (index: number) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index))
    // Очищаем ошибки при удалении файла
    if (fileErrors.length > 0) {
      setFileErrors([])
    }
    // Если удаляем последний файл, показываем ошибку
    if (
      uploadedFiles.length === 1 &&
      (!uploadedPreviouslyFiles || !uploadedPreviouslyFiles.length)
    ) {
      setFileRequiredError(true)
    }
  }

  const handleClose = () => {
    setUploadedFiles([])
    setFileErrors([])
    setFileRequiredError(false)
    onClose()
  }
  const handleDeleteFile = (fileId: string) => {
    setUploadedPreviouslyFiles((prev) => prev.filter((file) => file.id !== fileId))

    deleteReceipt({ withdrawId: withdraw.id, fileId })
  }
  // Генерируем уникальный key для input, чтобы он перерендеривался после каждой загрузки
  const inputKey = `file-input-${uploadedFiles.length}-${Date.now()}`

  return (
    <>
      <BaseDialog
        isVisible={isOpen}
        onClose={handleClose}
        onStepBack={onCancel}
        onAction={factAmount!<withdraw.amountTo&&confirm?()=>setShowFactAmountConfirm(true):handleSubmit}
        actionButtonName={isMobile ? 'Оплатить' : 'Подтвердить оплату'}
        closeButtonName={isMobile ? 'Отменить' : 'Открепить заявку'}
        closeButton
        isLoading={isLoading}
        iconTitle={
          <>
            <ItemLayout
              icon={<PayoutIcon status={WithdrawStatusIcons[withdraw.withdrawStatus]} />}
              title={
                <span className="mr-[10px]">{WithdrawStatusTitles[withdraw.withdrawStatus]}</span>
              }
              subtitle={formatDate(withdraw.acceptedByTraderAt)}
              timeLeft={withdraw.payingAllowedTill}
              statusForTime="inProgress"
            />
          </>
        }
      >
        <div className="flex flex-col gap-2 py-6 border-y-1 border-y-card-border dark:border-y-card-border-dark">
          <CopyBlock text={withdraw.id} title={withdraw.id} color="secondary">
            ID заявки: {withdraw.id}
          </CopyBlock>
          <div className="flex items-center gap-2">
            <span className="text-text-secondary">Сумма заявки:</span>
            <span className="font-medium text-[16px]">
              {formatCurrency(withdraw.amountTo, Currencies.RUB)}
            </span>
            <BaseCopyButton text={withdraw.amountTo.toString()} color="primary" />
          </div>
        </div>
        <div className="flex gap-2 items-center">
          <span className="text-text-secondary">Фактическая сумма</span>
          <BaseInput
            type="number"
            min={0}
            max={withdraw.amountTo}
            rightText={<div className="text-text-secondary ml-4">RUB</div>}
            optionInputClasses="[field-sizing:content] [&::-webkit-inner-spin-button]:appearance-none
               [&::-webkit-outer-spin-button]:appearance-none"
            value={factAmount}
            onChange={(v) => setFactAmount(+v.target.value)}
          />
        </div>
        {factAmount! > withdraw.amountTo && (
          <div className="text-error text-sm">
            Фактическая сумма не может быть больше суммы заявки
          </div>
        )}
        {(!factAmount || factAmount < 0) && (
          <div className="text-error text-sm">Фактическая сумма должна быть больше нуля</div>
        )}
        <div>
          <span className="inline-block mb-4 text-text-secondary">Реквизиты для оплаты</span>
          <div className="flex flex-col gap-[10px]">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-10 h-10">{bankIcon}</div>
              <span>{bankTitle}</span>
            </div>

            <div className="flex items-center gap-[10px]">
              <BaseInput
                className="grow"
                value={formatRequisite(withdraw.paymentRequisitValue)}
                disabled
              />
              <BaseCopyButton text={withdraw.paymentRequisitValue} color="primary" />
            </div>
            {withdraw.recipientFullName ? (
              <div className="flex items-center gap-[10px]">
                <BaseInput className="grow" value={withdraw.recipientFullName} disabled />
                <BaseCopyButton text={withdraw.recipientFullName} color="primary" />
              </div>
            ) : null}
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <label className="font-medium text-sm">
            Прикрепленные чеки (
            {uploadedPreviouslyFiles
              ? uploadedPreviouslyFiles.length + uploadedFiles.length
              : uploadedFiles.length}
            ) {fileRequiredError && <span className="text-error">*</span>}
          </label>

          {/* Сообщение об ошибке обязательного файла */}
          {fileRequiredError && (
            <div className="text-error text-sm">Необходимо прикрепить хотя бы один файл</div>
          )}
          {/* Список загруженных ранее файлов */}
          {uploadedPreviouslyFiles && uploadedPreviouslyFiles.length > 0 && (
            <div className="flex flex-col gap-2 mt-2">
              {uploadedPreviouslyFiles.map((file, index) => (
                <div
                  key={`${file.originalFileName}-${index}-${(file.totalBytesLenght / 1024).toFixed(2)}`}
                  className="flex justify-between items-center bg-light-purple dark:bg-dark-purple p-3 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-3 flex-1">
                      <FileViewDialog file={file}>
                        <p className="font-medium text-sm text-left">{file.originalFileName}</p>
                        <p className="text-xs text-text-secondary text-left">
                          {formatDate(file.createdAt)} • {(file.totalBytesLenght / 1024).toFixed(2)}{' '}
                          KB
                        </p>
                      </FileViewDialog>
                    </div>
                    <BaseButton
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteFile(file.id)}
                      className="ml-auto"
                    >
                      <div className="flex justify-center items-center bg-[#e5e5ff] dark:bg-[#2a2a3a] rounded-full min-w-8 h-8">
                        <TrashIcon className="text-error w-4 h-4" />
                      </div>
                    </BaseButton>
                  </div>
                </div>
              ))}
            </div>
          )}

          <label className="font-medium text-sm">Новые чеки</label>
          {/* Список загруженных сейчас файлов */}
          {uploadedFiles.length > 0 && (
            <div className="flex flex-col gap-2 mt-2">
              {uploadedFiles.map((file, index) => (
                <div
                  key={`${file.name}-${index}-${file.size}`}
                  className="flex justify-between items-center bg-light-purple dark:bg-dark-purple p-3 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-3 flex-1">
                      <FileViewDialog file={file}>
                        <p className="font-medium text-sm text-left">{file.name}</p>
                        <p className="text-xs text-text-secondary text-left">
                          {(file.size / 1024).toFixed(1)} KB
                        </p>
                      </FileViewDialog>
                    </div>
                    <BaseButton
                      variant="ghost"
                      size="icon"
                      onClick={() => handleFileRemove(index)}
                      className="ml-auto"
                    >
                      <div className="flex justify-center items-center bg-[#e5e5ff] dark:bg-[#2a2a3a] rounded-full min-w-8 h-8">
                        <TrashIcon className="text-error w-4 h-4" />
                      </div>
                    </BaseButton>
                  </div>
                </div>
              ))}
            </div>
          )}
          {/* Кнопка загрузки файлов - всегда видна */}
          <div className="relative">
            <input
              key={inputKey}
              type="file"
              multiple
              accept=".pdf"
              // accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.xls,.xlsx"
              onChange={handleFileChange}
              className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
              disabled={isUploading}
            />
            <BaseButton
              variant="transparent"
              padding="xs"
              className="disabled:opacity-50 text-primary hover:text-primary-hover dark:hover:text-primary-dark-hover dark:text-primary-dark"
              disabled={isUploading}
            >
              <CirclePlus width={20} height={20} />
              {isUploading
                ? 'Загрузка...'
                : uploadedFiles.length === 0
                  ? 'Прикрепить файлы'
                  : 'Добавить еще файлы'}
            </BaseButton>
          </div>
          {/* Отображение ошибок валидации файлов */}
          {fileErrors.length > 0 && (
            <div className="text-error text-sm">
              {fileErrors.map((error, index) => (
                <p key={index}>{error}</p>
              ))}
            </div>
          )}
        </div>

        <div className="relative flex gap-[10px] py-4 pr-5 pl-[50px] border-1 border-card-border dark:border-card-border-dark rounded-lg">
          <AttentionIcon className="top-4 left-5 absolute" />
          <div className="text-text-secondary text-sm grow">
            <p className="mb-4">
              Убедитесь, что перевели средства по указанным выше реквизитам, и сумма перевода была
              точно такая же, как указано в заявке.
            </p>
            <p>
              Площадка не несет ответственность за возврат отправленных средств на неверные
              реквизиты
            </p>
          </div>
        </div>
        <BaseCheckbox
          label="Я подтверждаю, что перевёл(а) точную сумму на указанные реквизиты и проверил(а) корректность платежа."
          checked={confirm}
          onChange={(v) => {
            setConfirm(v)
            setConfirmError(false)
          }}
          fieldClassName="text-sm gap-4"
          boxClassName={confirmError ? '!border-error !border-2' : ''}
          error={confirmError ? 'Подтвердите перевод' : undefined}
        />
      </BaseDialog>
      <BaseDialog
        isVisible={showFactAmountConfirm}
        onAction={handleSubmit}
        onClose={() => setShowFactAmountConfirm(false)}
        actionButtonName={'Да, подтвердить'}
        closeButtonName={'Вернуться к платежу'}
        closeButton
        iconTitle={
          <h1 className="text-error font-semibold">
            Внимание!
          </h1>
        }
      >
        <div>
          Вы уверены, что хотите закрыть выплату на{' '}<span className="text-primary">{formatCurrency(withdraw.amountTo, Currencies.RUB)}</span>{' '}
          суммой в <span className="text-primary">{formatCurrency(factAmount!, Currencies.RUB)}</span>
        </div>
      </BaseDialog>
    </>
  )
}
